export interface EmailCampaignIdentity {
  scope: string;
  fingerprint: string;
  recipients: string[];
  id: string;
  storageKey: string;
  pointerKey: string;
}

interface CampaignStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface StoredCampaignPointer {
  version: 1;
  id: string;
  fingerprint: string;
  recipients: string[];
  storageKey: string;
}

const STORAGE_PREFIX = "helpgenie:email-campaign:";
const INSTALLATION_NONCE_KEY = `${STORAGE_PREFIX}installation-nonce`;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const inMemoryValues = new Map<string, string>();

const defaultStorage = (): CampaignStorage | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

const sha256 = async (value: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const deterministicUuid = (hash: string): string => {
  const chars = hash.slice(0, 32).split("");
  chars[12] = "4";
  chars[16] = ((Number.parseInt(chars[16], 16) & 0x3) | 0x8).toString(16);
  const hex = chars.join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const withBrowserLock = async <T>(
  name: string,
  task: () => Promise<T>,
): Promise<T> => {
  const locks =
    typeof navigator === "undefined"
      ? undefined
      : (
          navigator as Navigator & {
            locks?: {
              request: <R>(
                name: string,
                callback: () => Promise<R>,
              ) => Promise<R>;
            };
          }
        ).locks;
  return locks ? locks.request(`${STORAGE_PREFIX}lock:${name}`, task) : task();
};

const normalizeRecipients = (recipients: string[]): string[] =>
  [
    ...new Set(
      recipients.map((email) => email.trim().toLowerCase()).filter(Boolean),
    ),
  ].sort();

const read = (
  storage: CampaignStorage | undefined,
  key: string,
): string | null => {
  try {
    return storage ? storage.getItem(key) : (inMemoryValues.get(key) ?? null);
  } catch {
    return inMemoryValues.get(key) ?? null;
  }
};

const write = (
  storage: CampaignStorage | undefined,
  key: string,
  value: string,
): void => {
  inMemoryValues.set(key, value);
  try {
    storage?.setItem(key, value);
  } catch {
    // In-memory reuse still protects retries while this page remains open.
  }
};

const remove = (storage: CampaignStorage | undefined, key: string): void => {
  inMemoryValues.delete(key);
  try {
    storage?.removeItem(key);
  } catch {
    // Ignore unavailable browser storage.
  }
};

const keysFor = async (scope: string, recipients: string[]) => {
  const canonicalRecipients = normalizeRecipients(recipients);
  const fingerprint = [scope, ...canonicalRecipients].join("\n");
  const [scopeHash, fingerprintHash] = await Promise.all([
    sha256(scope),
    sha256(fingerprint),
  ]);
  return {
    canonicalRecipients,
    fingerprint,
    fingerprintHash,
    storageKey: `${STORAGE_PREFIX}id:${fingerprintHash}`,
    pointerKey: `${STORAGE_PREFIX}pending:${scopeHash}`,
  };
};

const getInstallationNonce = async (
  storage: CampaignStorage | undefined,
): Promise<string> => {
  const existing = read(storage, INSTALLATION_NONCE_KEY);
  if (existing && UUID_PATTERN.test(existing)) return existing;

  return withBrowserLock("installation", async () => {
    const lockedExisting = read(storage, INSTALLATION_NONCE_KEY);
    if (lockedExisting && UUID_PATTERN.test(lockedExisting)) {
      return lockedExisting;
    }
    const nonce = crypto.randomUUID();
    write(storage, INSTALLATION_NONCE_KEY, nonce);
    return read(storage, INSTALLATION_NONCE_KEY) ?? nonce;
  });
};

/**
 * Persists a pending send across tabs, reloads, and browser restarts. Storage
 * keys contain hashes only. The normalized recipient list is retained in the
 * pointer value solely so a lost-response campaign can be reconstructed, and
 * is removed on full delivery or an explicit guarded replacement.
 *
 * The first ID is derived from the fingerprint plus a persisted random browser
 * installation nonce. Simultaneous tabs converge, while a different browser
 * or cleared storage cannot resurrect an old server campaign ID.
 */
export const getOrCreateEmailCampaign = async (
  scope: string,
  recipients: string[],
  storage: CampaignStorage | undefined = defaultStorage(),
): Promise<EmailCampaignIdentity> => {
  const keys = await keysFor(scope, recipients);
  const id = await withBrowserLock(keys.storageKey, async () => {
    const stored = read(storage, keys.storageKey);
    if (stored && UUID_PATTERN.test(stored)) return stored;
    const installationNonce = await getInstallationNonce(storage);
    const initialId = deterministicUuid(
      await sha256(`${keys.fingerprintHash}\n${installationNonce}`),
    );
    write(storage, keys.storageKey, initialId);
    return read(storage, keys.storageKey) ?? initialId;
  });
  const pointer: StoredCampaignPointer = {
    version: 1,
    id,
    fingerprint: keys.fingerprint,
    recipients: keys.canonicalRecipients,
    storageKey: keys.storageKey,
  };

  write(storage, keys.storageKey, id);
  write(storage, keys.pointerKey, JSON.stringify(pointer));
  return {
    scope,
    fingerprint: keys.fingerprint,
    recipients: keys.canonicalRecipients,
    id,
    storageKey: keys.storageKey,
    pointerKey: keys.pointerKey,
  };
};

export const loadPendingEmailCampaign = async (
  scope: string,
  storage: CampaignStorage | undefined = defaultStorage(),
): Promise<EmailCampaignIdentity | null> => {
  const pointerKey = `${STORAGE_PREFIX}pending:${await sha256(scope)}`;
  const raw = read(storage, pointerKey);
  if (!raw) return null;

  try {
    const pointer = JSON.parse(raw) as Partial<StoredCampaignPointer>;
    if (
      pointer.version !== 1 ||
      !UUID_PATTERN.test(pointer.id ?? "") ||
      !Array.isArray(pointer.recipients) ||
      pointer.recipients.some((email) => typeof email !== "string")
    ) {
      remove(storage, pointerKey);
      return null;
    }
    const campaign = await getOrCreateEmailCampaign(
      scope,
      pointer.recipients,
      storage,
    );
    if (campaign.id !== pointer.id) {
      remove(storage, pointerKey);
      return null;
    }
    return campaign;
  } catch {
    remove(storage, pointerKey);
    return null;
  }
};

/** Rotate the stored ID so a later intentional send with the same list is new. */
export const clearEmailCampaign = (
  campaign: EmailCampaignIdentity,
  storage: CampaignStorage | undefined = defaultStorage(),
): void => {
  if (read(storage, campaign.storageKey) === campaign.id) {
    write(storage, campaign.storageKey, crypto.randomUUID());
  }

  const rawPointer = read(storage, campaign.pointerKey);
  try {
    const pointer = rawPointer
      ? (JSON.parse(rawPointer) as StoredCampaignPointer)
      : null;
    if (pointer?.id === campaign.id) remove(storage, campaign.pointerKey);
  } catch {
    remove(storage, campaign.pointerKey);
  }
};
